import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  sandboxApi,
  getSandboxToken,
  setSandboxToken,
  setSandboxUser,
} from './sandboxApi';

const ROLE_CONFIG = {
  funcionario: {
    title: 'Sandbox · Funcionario',
    subtitle: 'Ambiente de capacitación para Funcionarios',
    defaultEmail: 'funcionario.sandbox@innpulsa.com',
    accent: 'from-sky-600 to-indigo-700',
  },
  validador: {
    title: 'Sandbox · Validador',
    subtitle: 'Ambiente de capacitación para Validadores',
    defaultEmail: 'validador.sandbox@innpulsa.com',
    accent: 'from-emerald-600 to-teal-700',
  },
};

export default function SandboxLogin() {
  const location = useLocation();
  const role = location.pathname.includes('validador') ? 'validador' : 'funcionario';
  const navigate = useNavigate();
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.funcionario;

  const [email, setEmail] = useState(cfg.defaultEmail);
  const [password, setPassword] = useState('Sandbox2026');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEmail(cfg.defaultEmail);
  }, [role, cfg.defaultEmail]);

  // If already logged into sandbox, jump to the right dashboard
  useEffect(() => {
    const t = getSandboxToken();
    if (t) {
      sandboxApi
        .me()
        .then((res) => {
          const ut = res.data.user_type;
          if (ut === 'funcionario') navigate('/sandbox/funcionario/dashboard');
          else if (ut === 'validador') navigate('/sandbox/validador/dashboard');
        })
        .catch(() => {});
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await sandboxApi.login(email, password);
      setSandboxToken(data.access_token);
      setSandboxUser(data.user);
      toast.success(`Bienvenido al sandbox, ${data.user.first_name}`);
      if (data.user.user_type === 'funcionario') {
        navigate('/sandbox/funcionario/dashboard');
      } else if (data.user.user_type === 'validador') {
        navigate('/sandbox/validador/dashboard');
      } else {
        toast.error('Rol no soportado en sandbox');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo iniciar sesión';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <div className="bg-amber-500 text-amber-950 text-center py-2 font-bold tracking-wider text-sm uppercase">
        Ambiente de Capacitación · Sandbox aislado
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <Card
          data-testid={`sandbox-login-card-${role}`}
          className="w-full max-w-md border-slate-700 bg-slate-900 text-slate-100 shadow-2xl"
        >
          <div className={`h-2 rounded-t-lg bg-gradient-to-r ${cfg.accent}`} />
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">{cfg.title}</CardTitle>
            <p className="text-sm text-slate-400 mt-1">{cfg.subtitle}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="sandbox-email" className="text-slate-300">
                  Correo
                </Label>
                <Input
                  id="sandbox-email"
                  data-testid="sandbox-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sandbox-password" className="text-slate-300">
                  Contraseña
                </Label>
                <Input
                  id="sandbox-password"
                  data-testid="sandbox-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100"
                  required
                />
              </div>
              <Button
                data-testid="sandbox-login-submit-btn"
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${cfg.accent} hover:opacity-90`}
              >
                {loading ? 'Ingresando...' : 'Ingresar al sandbox'}
              </Button>
              <div className="text-xs text-slate-400 bg-slate-800/60 rounded-md p-3 leading-relaxed">
                <strong className="text-amber-400">Credenciales compartidas:</strong>
                <br />
                <code className="text-slate-200">{cfg.defaultEmail}</code>
                <br />
                Contraseña: <code className="text-slate-200">Sandbox2026</code>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
